import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const sections = [
  {
    id: 'intro',
    title: '1. Introduction & Scope',
    content: `Uni5d Technology Solutions LLC ("Uni5d," "we," "us," or "our") operates the Quali5d AI Recruitment Intelligence platform ("Platform") at www.quali5d.com. This Privacy Policy explains how we collect, use, store, protect, and share information when you use our Platform.

This Policy applies to:
• Business customers ("Customers") who subscribe to and use the Platform
• Hiring managers who participate in AI intake calls via the Platform
• Job candidates who participate in AI screening interviews via the Platform
• Visitors to www.quali5d.com

This Policy does not apply to the employment practices of our Customers. If you are a candidate who participated in an AI interview arranged by a Quali5d Customer, you should also review that employer's privacy policy.

Effective Date: May 4, 2026
Last Updated: May 4, 2026`
  },
  {
    id: 'collected',
    title: '2. Information We Collect',
    content: `2.1 Information You Provide Directly

Account Registration:
• Company name, address (including city, state/province, postal code, country)
• Administrator full name, designation, work email address
• Phone number (including country code)
• Tax registration number (EIN/VAT)
• Company logo
• Password (stored as a one-way cryptographic hash — never in plain text)

Billing Information:
• Payment card details are collected and processed exclusively by Stripe, Inc. We do not store card numbers, CVV codes, or full payment credentials on our servers.
• Billing address, transaction history

Platform Content (Customer Data):
• Job description documents (PDF, DOCX, TXT)
• Audio and video recordings of intake meetings
• Text transcripts generated from recordings
• AI-generated requirement documents, candidate screeners, and screening questions
• Candidate information submitted by Customers for interview scheduling

2.2 Information Collected Automatically

When you use the Platform, we automatically collect:
• Log data: IP addresses, browser type, operating system, pages visited, timestamps
• Session data: Authentication tokens, session duration
• Usage analytics: Features used, errors encountered, performance metrics
• Device information: Screen resolution, browser version

2.3 Information from Third Parties

• Stripe, Inc.: Payment confirmation, transaction status, fraud signals
• Microsoft Azure: Infrastructure performance and availability metrics`
  },
  {
    id: 'use',
    title: '3. How We Use Your Information',
    content: `We use collected information for the following purposes:

Service Delivery:
• Processing job descriptions and generating AI requirement documents
• Conducting AI intake calls and candidate interviews
• Generating transcripts, assessments, and reports
• Managing your account, subscription, and billing

AI Processing:
• Audio and video recordings are transcribed using Microsoft Azure Speech Services
• Job descriptions, transcripts, and requirements are analyzed by Anthropic's Claude API to generate documents and scoring
• Phase 2 and Phase 3 conversations are conducted via ElevenLabs' conversational AI

Platform Improvement:
• Analyzing usage patterns to improve features and performance
• Training and improving our proprietary scoring models using anonymized, aggregated data only
• Note: We do not use identifiable Customer Data or candidate personal data to train AI models without explicit consent

Communications:
• Transactional emails (account activation, subscription confirmations, payment receipts)
• Service notifications (usage limits, updates, maintenance)
• Support responses

Legal & Compliance:
• Complying with applicable laws and regulations
• Responding to lawful requests from government authorities
• Enforcing our Terms of Service and Acceptable Use Policy
• Preventing fraud and security threats`
  },
  {
    id: 'ai-processing',
    title: '4. AI & Automated Processing',
    content: `4.1 How AI Processes Your Data

Quali5d uses AI systems to analyze content and generate outputs. This involves:

Phase 1 (JD Analysis): Job descriptions and meeting recordings are sent to Anthropic's Claude API for analysis and document generation.

Phase 2 (AI Intake Calls): Conversations with hiring managers are conducted by ElevenLabs AI agents and recorded in real time. Audio is transcribed and analyzed.

Phase 3 (AI Candidate Interviews): Candidate interviews are conducted by ElevenLabs AI agents, recorded, transcribed, and scored using our proprietary algorithm powered by Anthropic's Claude API.

4.2 Automated Decision-Making

Quali5d generates candidate scores and hiring recommendations through automated processing. In accordance with:
• GDPR Article 22: EU/EEA individuals have the right not to be subject to decisions based solely on automated processing that produces legal or similarly significant effects. Quali5d's outputs are advisory tools — final hiring decisions must involve human review.
• Similar rights exist under applicable US state laws.

Our Customers are responsible for ensuring that AI-generated scores are used in compliance with applicable employment and AI regulation laws in their jurisdiction, including bias audit requirements.

4.3 Recording Notice

All Phase 2 and Phase 3 sessions are audio/video recorded. Customers are contractually required to:
• Inform all participants that the session is AI-conducted and will be recorded
• Obtain explicit consent from candidates before scheduling AI interviews
• Maintain records of consent as required by applicable law

4.4 Sub-Processors for AI

| Sub-Processor | Purpose | Location |
|---|---|---|
| Anthropic, Inc. | Document generation, candidate scoring | United States |
| ElevenLabs, Inc. | AI voice conversations (Jessica, Alex) | United States |
| Microsoft Azure | Speech transcription, infrastructure | United States |
| Stripe, Inc. | Payment processing | United States |`
  },
  {
    id: 'retention',
    title: '5. Data Retention',
    content: `We retain different categories of data for different periods:

Audio & Video Recordings:
Retained for 90 days from the date of upload or session completion. After 90 days, recordings are permanently and irreversibly deleted from all our systems including backups.

Transcripts & AI-Generated Documents:
Retained for the duration of your active subscription plus 30 days following account termination.

Account & Billing Information:
Retained for 7 years following the end of the subscription for tax and accounting compliance purposes (required under applicable law).

Usage Logs & Analytics:
Retained for 12 months in identifiable form, then aggregated and anonymized.

Backup Data:
Encrypted backups are retained for up to 30 days. Data deleted from the primary system is purged from backups within this window.

Upon Account Termination:
All Customer Data (except billing records required by law) is permanently deleted within 30 days of account termination.

Data Deletion Requests:
You may request deletion of specific data prior to standard retention expiry by contacting admin@uni5d.ai. We will process valid deletion requests within 30 days, subject to legal retention requirements.`
  },
  {
    id: 'sharing',
    title: '6. Data Sharing & Disclosure',
    content: `We do not sell your personal data. We do not share Customer Data with third parties for marketing purposes.

We share data only in the following circumstances:

Service Providers (Sub-Processors):
We share data with the sub-processors listed in Section 4.4 solely for the purpose of providing Platform services. All sub-processors are contractually bound to protect data and process it only as instructed.

Legal Requirements:
We may disclose data when required by law, subpoena, court order, or other legal process, or when we believe disclosure is necessary to protect the rights, property, or safety of Uni5d, our customers, or the public. We will notify affected Customers of such disclosures where legally permitted.

Business Transfers:
In the event of a merger, acquisition, or sale of Uni5d's assets, Customer Data may be transferred to the successor entity. We will notify Customers prior to data transfer and provide the option to delete their data.

With Your Consent:
We may share data for purposes not described here with your explicit prior consent.

Aggregate & Anonymized Data:
We may share aggregated, anonymized statistics (e.g., "X% of companies using AI intake calls reduce time-to-hire by Y%") that cannot reasonably identify any individual or company.`
  },
  {
    id: 'security',
    title: '7. Data Security',
    content: `We implement industry-standard technical and organizational security measures to protect your data:

Encryption:
• All data transmitted to and from the Platform uses TLS 1.2 or higher
• Data at rest is encrypted using AES-256 encryption
• Database connections use encrypted channels

Access Controls:
• Role-based access control (RBAC) limits data access to authorized personnel
• Multi-factor authentication is required for administrative access
• Regular access reviews and privilege minimization

Infrastructure Security:
• Platform hosted on Microsoft Azure with SOC 2 Type II certified infrastructure
• Regular security scanning and vulnerability assessments
• Security headers and CSP policies implemented

Incident Response:
• In the event of a data breach affecting your data, we will notify you within 72 hours of discovery, as required by applicable law, including GDPR.

Limitations:
No security system is impenetrable. We cannot guarantee absolute security. You are responsible for maintaining the confidentiality of your account credentials.`
  },
  {
    id: 'rights',
    title: '8. Your Rights',
    content: `Depending on your location, you may have the following rights regarding your personal data:

Right to Access:
Request a copy of the personal data we hold about you or your organization.

Right to Rectification:
Request correction of inaccurate personal data.

Right to Erasure ("Right to Be Forgotten"):
Request deletion of your personal data, subject to legal retention requirements.

Right to Data Portability:
Request your data in a machine-readable format.

Right to Object:
Object to processing of your personal data for certain purposes.

Right to Restrict Processing:
Request restriction of processing in certain circumstances.

Rights Regarding Automated Decision-Making:
Request human review of any decision made solely through automated AI processing that significantly affects you.

For EU/EEA Residents (GDPR):
All the above rights apply. Our lawful basis for processing is: contract performance (service delivery), legitimate interests (platform improvement, security), and legal obligation (compliance).

For California Residents (CCPA/CPRA):
You have the right to know, delete, opt-out of sale (we do not sell data), and non-discrimination.

For Indian Residents (DPDP Act):
You have rights under India's Digital Personal Data Protection Act 2023 as applicable.

To Exercise Your Rights:
Contact us at admin@uni5d.ai with the subject line "Privacy Rights Request." We will respond within 30 days.`
  },
  {
    id: 'international',
    title: '9. International Data Transfers',
    content: `Uni5d Technology Solutions LLC is based in the United States. If you access the Platform from outside the United States, your data will be transferred to and processed in the United States.

For transfers from the EU/EEA or UK, we rely on Standard Contractual Clauses (SCCs) approved by the European Commission as the lawful transfer mechanism.

Our sub-processors (Anthropic, ElevenLabs, Microsoft Azure, Stripe) are US-based companies. Data transferred to these processors is protected by contractual data processing agreements incorporating appropriate safeguards.

By using the Platform, you acknowledge that data may be transferred to, stored in, and processed in the United States, which may have different data protection laws than your country of residence.`
  },
  {
    id: 'cookies',
    title: '10. Cookies & Tracking',
    content: `We use the following types of cookies and local storage:

Essential (Required):
• Authentication tokens — maintains your login session
• Session preferences — timezone, UI settings
These cannot be disabled as they are necessary for the Platform to function.

Analytics (Optional):
• Usage analytics to understand feature adoption and improve the Platform
• You may opt out by contacting admin@uni5d.ai

We do not use advertising cookies or third-party tracking for marketing purposes. We do not participate in behavioral advertising networks.

Browser Controls:
You can control cookies through your browser settings. Disabling essential cookies will prevent you from logging in to the Platform.`
  },
  {
    id: 'children',
    title: '11. Children\'s Privacy',
    content: `The Quali5d Platform is a B2B service intended for use by organizations and their professional employees. The Platform is not directed to, and we do not knowingly collect personal information from, children under the age of 18.

If you believe a child under 18 has provided personal information to us, please contact admin@uni5d.ai immediately and we will take steps to delete such information.`
  },
  {
    id: 'changes',
    title: '12. Changes to This Policy',
    content: `We may update this Privacy Policy from time to time. We will notify you of material changes by:
• Sending an email to your registered account email address at least 30 days before changes take effect
• Displaying a prominent notice on the Platform

Your continued use of the Platform after the effective date of the revised Policy constitutes your acceptance of the changes.

For non-material changes (e.g., corrections, clarifications), we will update the "Last Updated" date without prior notice.`
  },
  {
    id: 'contact',
    title: '13. Contact & Data Controller',
    content: `Data Controller:
Uni5d Technology Solutions LLC
Mooresville, North Carolina, United States
EIN: 42-1896513

Privacy Contact:
Email: admin@uni5d.ai
Subject line: "Privacy Policy Inquiry"

Response Time: We aim to respond to all privacy inquiries within 5 business days and to formal rights requests within 30 days.

For EU/EEA residents, if you believe we have not adequately addressed your privacy concerns, you have the right to lodge a complaint with your local supervisory authority.`
  }
];

export default function PrivacyPage() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('intro');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Privacy Policy — Quali5d AI Recruitment Intelligence</title>
        <meta name="description" content="How Quali5d collects, uses, and protects personal data. GDPR-compliant, CCPA-compliant, with clear data retention policies and candidate consent requirements." />
        <link rel="canonical" href="https://www.quali5d.com/privacy" />
        <meta property="og:title" content="Privacy Policy — Quali5d AI Recruitment Intelligence" />
        <meta property="og:description" content="How Quali5d collects, uses, and protects personal data. GDPR-compliant, CCPA-compliant, with clear data retention policies and candidate consent requirements." />
        <meta property="og:url" content="https://www.quali5d.com/privacy" />
        <meta name="twitter:title" content="Privacy Policy — Quali5d AI Recruitment Intelligence" />
        <meta name="twitter:description" content="How Quali5d collects, uses, and protects personal data. GDPR-compliant, CCPA-compliant, with clear data retention policies and candidate consent requirements." />
      </Helmet>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
            </svg>
            <span className="text-sm font-medium">Back to Quali5d</span>
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-black text-xs">Q5</span>
            </div>
            <span className="font-bold text-gray-900 text-sm">Quali5d</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 rounded-full px-4 py-1.5 mb-4">
            <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/>
            </svg>
            <span className="text-xs font-semibold text-green-700">Legal Document</span>
          </div>
          <h1 className="text-4xl font-black text-gray-900 mb-3">Privacy Policy</h1>
          <p className="text-gray-500 text-lg">How Uni5d Technology Solutions LLC collects, uses, and protects your data</p>
          <div className="flex flex-wrap gap-4 mt-4 text-sm text-gray-500">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-green-500 rounded-full"></span>Effective: May 4, 2026</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-blue-500 rounded-full"></span>GDPR & CCPA Compliant</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-purple-500 rounded-full"></span>Audio/Video: 90-day retention</span>
          </div>
        </div>

        <div className="flex gap-8">
          {/* Sidebar TOC */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24 bg-white rounded-2xl border border-gray-200 p-4 max-h-[calc(100vh-120px)] overflow-y-auto">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 px-2">Contents</p>
              <nav className="space-y-0.5">
                {sections.map(s => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    onClick={() => setActiveSection(s.id)}
                    className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                      activeSection === s.id
                        ? 'bg-green-50 text-green-700 font-semibold'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    {s.title}
                  </a>
                ))}
              </nav>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="space-y-6">
              {sections.map(s => (
                <div
                  key={s.id}
                  id={s.id}
                  className="bg-white rounded-2xl border border-gray-200 p-8"
                  onMouseEnter={() => setActiveSection(s.id)}
                >
                  <h2 className="text-xl font-bold text-gray-900 mb-4 pb-3 border-b border-gray-100">{s.title}</h2>
                  <div className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">{s.content}</div>
                </div>
              ))}
            </div>

            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-2xl p-6">
              <div className="flex gap-3">
                <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
                </svg>
                <div>
                  <p className="text-sm font-semibold text-blue-800 mb-1">Your Data Rights</p>
                  <p className="text-sm text-blue-700">To exercise any of your privacy rights, email us at <a href="mailto:admin@uni5d.ai" className="font-semibold underline">admin@uni5d.ai</a> with subject line "Privacy Rights Request." We respond within 30 days.</p>
                </div>
              </div>
            </div>

            <div className="mt-6 text-center text-sm text-gray-400">
              © 2026 Uni5d Technology Solutions LLC · EIN: 42-1896513 · Mooresville, North Carolina<br/>
              Questions? <a href="mailto:admin@uni5d.ai" className="text-blue-600 hover:underline">admin@uni5d.ai</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}